import React from 'react';

export type InputType = 'text' | 'email' | 'password' | 'number' | 'tel' | 'date' | 'time';
export type InputSize = 'sm' | 'md' | 'lg';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  helper?: string;
  size?: InputSize;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

const sizeStyles: Record<InputSize, string> = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-base',
  lg: 'px-4 py-3 text-lg',
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helper,
      size = 'md',
      icon,
      iconPosition = 'left',
      type = 'text',
      disabled,
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            {label}
            {props.required && <span className="text-error ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {icon && iconPosition === 'left' && (
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500 pointer-events-none">
              {icon}
            </div>
          )}

          <input
            ref={ref}
            type={type}
            disabled={disabled}
            className={`w-full rounded-lg border-2 font-sans transition-all duration-200 focus:outline-none ${icon && iconPosition === 'left' ? 'pl-10' : ''} ${icon && iconPosition === 'right' ? 'pr-10' : ''} ${error ? 'border-error bg-red-50 focus:ring-2 focus:ring-error focus:ring-offset-0' : 'border-neutral-300 bg-neutral-0 hover:border-neutral-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-100'} ${disabled ? 'bg-neutral-100 text-neutral-500 cursor-not-allowed border-neutral-200' : ''} ${sizeStyles[size]} ${className || ''}`}
            {...props}
          />

          {icon && iconPosition === 'right' && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-neutral-500 pointer-events-none">
              {icon}
            </div>
          )}
        </div>

        {error && (
          <p className="mt-2 text-sm font-medium text-error">{error}</p>
        )}

        {helper && !error && (
          <p className="mt-2 text-sm text-neutral-600">{helper}</p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
